import type { FilterableProduct } from "../config";
import { ATTRIBUTE_CONFIGS, getAttributeConfig } from "../config";
import type { SelectedAttributes } from "../types";

const FILTERABLE_ATTRIBUTE_CODES = ATTRIBUTE_CONFIGS.map((config) => config.attributeCode);

export type OptionCounts = Map<string, number>;

type OptionCountsByCode = Record<string, OptionCounts>;

type Selection = [attributeCode: string, selectedValues: string[]];

export const getProductValuesForCode = (product: FilterableProduct, attributeCode: string): string[] =>
  getAttributeConfig(attributeCode)?.getValues(product) ?? [];

const isFilterableCode = (attributeCode: string): boolean => Boolean(getAttributeConfig(attributeCode));

const productSatisfiesSelection = (product: FilterableProduct, [attributeCode, selectedValues]: Selection): boolean => {
  if (selectedValues.length === 0) return true;
  if (!isFilterableCode(attributeCode)) return true;

  // A product with no value here is left out on purpose: asking for example "Mint" flavor must not
  // return products whose flavor is simply unknown.
  return getProductValuesForCode(product, attributeCode).some((value) => selectedValues.includes(value));
};

const toSelections = (selected: SelectedAttributes): Selection[] => Object.entries(selected);

const exceptAttribute = (selections: Selection[], attributeCode?: string): Selection[] =>
  attributeCode === undefined ? selections : selections.filter(([code]) => code !== attributeCode);

/**
 * A product matches when it satisfies every filtered attribute (AND), and for each of them
 * carries at least one of the picked values (OR).
 * Pass `skipAttributeCode` to answer "would this product match if we ignored that one attribute?".
 * The drawer uses it so the options of the attribute you are currently editing stay clickable.
 */
export const productMatchesSelection = <T extends FilterableProduct>(
  product: T,
  selected: SelectedAttributes,
  skipAttributeCode?: string
): boolean =>
  exceptAttribute(toSelections(selected), skipAttributeCode).every((selection) =>
    productSatisfiesSelection(product, selection)
  );

export const filterProductsByAttributes = <T extends FilterableProduct>(
  products: T[],
  selected: SelectedAttributes
): T[] => {
  const nothingSelected = Object.keys(selected).length === 0;
  if (nothingSelected) return products;

  return products.filter((product) => productMatchesSelection(product, selected));
};

/** Products matching everything except the given attribute, i.e. the pool its own options count from. */
const getProductsMatchingOtherAttributes = <T extends FilterableProduct>(
  products: T[],
  selected: SelectedAttributes,
  attributeCode: string
): T[] => products.filter((product) => productMatchesSelection(product, selected, attributeCode));

/**
 * Counting how many products each option value would return
 */
const countProductsPerValue = <T extends FilterableProduct>(products: T[], attributeCode: string): OptionCounts => {
  const counts: OptionCounts = new Map();

  products.forEach((product) => {
    // A value repeated on one product, e.g. "466,466", must still count that product once.
    const distinctValues = new Set(getProductValuesForCode(product, attributeCode));

    distinctValues.forEach((value) => {
      counts.set(value, (counts.get(value) ?? 0) + 1);
    });
  });

  return counts;
};

/**
 * For every filterable attribute, how many products each of its option values would return right now. *
 * An attribute never counts against itself, so picking one flavour does not hide the others.
 * Every other picked attribute does narrow the count, exactly like it narrows the product grid,
 * which is why a value that ends up at 0 is shown as disabled.
 */
export const getOptionCountsByCode = <T extends FilterableProduct>(
  products: T[],
  selected: SelectedAttributes
): OptionCountsByCode =>
  FILTERABLE_ATTRIBUTE_CODES.reduce<OptionCountsByCode>((countsByCode, attributeCode) => {
    const countablePool = getProductsMatchingOtherAttributes(products, selected, attributeCode);

    countsByCode[attributeCode] = countProductsPerValue(countablePool, attributeCode);
    return countsByCode;
  }, {});

/* -------------------------------------------------------------------------- */
/* Pruning picked values that stopped matching                                */
/* -------------------------------------------------------------------------- */

type PrunePass = {
  selected: SelectedAttributes;
  droppedSomething: boolean;
};

/** Keeps only the values that still return at least one product */
const keepValuesWithResults = (values: string[], counts: OptionCounts | undefined): string[] =>
  values.filter((value) => (counts?.get(value) ?? 0) > 0);

/**
 * The values of one attribute that survive a pass. The anchor attribute is the one the shopper just
 * clicked, so its values are always kept and conflicts are resolved on the other attributes.
 */
const getSurvivingValues = (
  [attributeCode, values]: Selection,
  countsByCode: OptionCountsByCode,
  anchorAttributeCode: string
): string[] =>
  attributeCode === anchorAttributeCode ? values : keepValuesWithResults(values, countsByCode[attributeCode]);

/**
 * One pruning pass: recounts the options for the current selection and keeps only the values that
 * still return at least one product, dropping attributes that end up with no values at all.
 * Reports whether anything was dropped, so the caller knows if another pass is needed.
 */
const dropValuesWithoutResults = <T extends FilterableProduct>(
  products: T[],
  selected: SelectedAttributes,
  anchorAttributeCode: string
): PrunePass => {
  const countsByCode = getOptionCountsByCode(products, selected);
  const kept: SelectedAttributes = {};
  let droppedSomething = false;

  toSelections(selected).forEach((selection) => {
    const [attributeCode, values] = selection;
    const survivingValues = getSurvivingValues(selection, countsByCode, anchorAttributeCode);

    if (survivingValues.length < values.length) droppedSomething = true;
    if (survivingValues.length > 0) kept[attributeCode] = survivingValues;
  });

  return { selected: kept, droppedSomething };
};

/**
 * Drops picked values that no longer return anything, so a ticked option is never greyed out.
 * Without this, the only way to leave a dead-end selection would be Reset, because the options
 * that caused it would be disabled and therefore impossible to untick.
 *
 * `anchorAttributeCode` is the attribute the shopper just clicked. Its values are kept as they are
 * and the conflicting values are removed from the other attributes, so the last action always wins.
 */
export const pruneIncompatibleSelections = <T extends FilterableProduct>(
  products: T[],
  selected: SelectedAttributes,
  anchorAttributeCode: string
): SelectedAttributes => {
  let current = selected;
  let pass = dropValuesWithoutResults(products, current, anchorAttributeCode);

  // Dropping one value can leave another one without matches, so we repeat until nothing changes.
  while (pass.droppedSomething) {
    current = pass.selected;
    pass = dropValuesWithoutResults(products, current, anchorAttributeCode);
  }

  return current;
};
